import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FiHome, FiRotateCcw, FiInfo } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const HelicopterGame = () => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameState, setGameState] = useState('playing'); // playing, crashed, paused
  const [showInstructions, setShowInstructions] = useState(true);
  const gameLoopRef = useRef(null);
  
  // Game objects
  const helicopter = useRef({
    x: 100,
    y: 300,
    vx: 0,
    vy: 0,
    angle: 0,
    size: 40
  });
  
  const clouds = useRef([]);
  const obstacles = useRef([]);
  const keys = useRef({});

  // Initialize game
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Initialize clouds
    clouds.current = [];
    for (let i = 0; i < 15; i++) {
      clouds.current.push({
        x: Math.random() * canvas.width * 2,
        y: Math.random() * canvas.height,
        size: 20 + Math.random() * 40,
        speed: 0.5 + Math.random() * 1.5,
        opacity: 0.3 + Math.random() * 0.4
      });
    }
    
    // Initialize obstacles
    obstacles.current = [];
    for (let i = 0; i < 8; i++) {
      obstacles.current.push({
        x: 400 + i * 300,
        y: 100 + Math.random() * 400,
        width: 20,
        height: 80 + Math.random() * 100,
        speed: 1
      });
    }
    
    // Event listeners
    const handleKeyDown = (e) => {
      keys.current[e.key.toLowerCase()] = true;
      if (e.key === ' ') {
        e.preventDefault();
        if (gameState === 'crashed') {
          resetGame();
        }
      }
    };
    
    const handleKeyUp = (e) => {
      keys.current[e.key.toLowerCase()] = false;
    };
    
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      // Smooth mouse following
      const targetX = mouseX - 100;
      const targetY = mouseY;
      
      helicopter.current.vx += (targetX - helicopter.current.x) * 0.01;
      helicopter.current.vy += (targetY - helicopter.current.y) * 0.01;
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    canvas.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      canvas.removeEventListener('mousemove', handleMouseMove);
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [gameState]);
  
  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const gameLoop = () => {
      // Clear canvas
      ctx.fillStyle = 'linear-gradient(to bottom, #87CEEB, #98D8E8)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#87CEEB');
      gradient.addColorStop(0.7, '#98D8E8');
      gradient.addColorStop(1, '#F0F8FF');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw clouds
      clouds.current.forEach(cloud => {
        cloud.x -= cloud.speed;
        if (cloud.x < -cloud.size) {
          cloud.x = canvas.width + cloud.size;
          cloud.y = Math.random() * canvas.height;
        }
        
        ctx.fillStyle = `rgba(255, 255, 255, ${cloud.opacity})`;
        ctx.beginPath();
        ctx.arc(cloud.x, cloud.y, cloud.size, 0, Math.PI * 2);
        ctx.fill();
        
        // Add some cloud detail
        ctx.beginPath();
        ctx.arc(cloud.x - cloud.size * 0.3, cloud.y, cloud.size * 0.7, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cloud.x + cloud.size * 0.3, cloud.y, cloud.size * 0.8, 0, Math.PI * 2);
        ctx.fill();
      });
      
      // Handle helicopter input
      const heli = helicopter.current;
      
      // Keyboard controls
      if (keys.current['w'] || keys.current['arrowup']) heli.vy -= 0.8;
      if (keys.current['s'] || keys.current['arrowdown']) heli.vy += 0.8;
      if (keys.current['a'] || keys.current['arrowleft']) heli.vx -= 0.5;
      if (keys.current['d'] || keys.current['arrowright']) heli.vx += 0.5;
      
      // Apply physics
      heli.vy += 0.3; // gravity
      heli.vx *= 0.98; // air resistance
      heli.vy *= 0.98;
      
      // Update position
      heli.x += heli.vx;
      heli.y += heli.vy;
      
      // Update angle based on velocity
      heli.angle = Math.atan2(heli.vy, heli.vx) * 0.1;
      
      // Boundary checking
      if (heli.x < 0) heli.x = 0;
      if (heli.x > canvas.width - heli.size) heli.x = canvas.width - heli.size;
      if (heli.y < 0) heli.y = 0;
      if (heli.y > canvas.height - heli.size) {
        setGameState('crashed');
        return;
      }
      
      // Update and draw obstacles
      obstacles.current.forEach((obstacle, index) => {
        obstacle.x -= obstacle.speed;
        
        if (obstacle.x < -obstacle.width) {
          obstacle.x = canvas.width;
          obstacle.y = 100 + Math.random() * 400;
          setScore(prev => prev + 10);
        }
        
        // Draw obstacle (mountain/tower)
        ctx.fillStyle = '#8B4513';
        ctx.fillRect(obstacle.x, obstacle.y, obstacle.width, obstacle.height);
        ctx.fillStyle = '#A0522D';
        ctx.fillRect(obstacle.x, obstacle.y, obstacle.width * 0.8, obstacle.height * 0.8);
        
        // Collision detection
        if (heli.x + heli.size > obstacle.x && 
            heli.x < obstacle.x + obstacle.width &&
            heli.y + heli.size > obstacle.y && 
            heli.y < obstacle.y + obstacle.height) {
          setGameState('crashed');
          return;
        }
      });
      
      // Draw helicopter
      ctx.save();
      ctx.translate(heli.x + heli.size/2, heli.y + heli.size/2);
      ctx.rotate(heli.angle);
      
      // Helicopter body
      ctx.fillStyle = '#17214a'; // Blacksand navy color
      ctx.fillRect(-heli.size/2, -heli.size/4, heli.size, heli.size/2);
      
      // Cockpit
      ctx.fillStyle = '#9fcbdc'; // Aero blue
      ctx.fillRect(-heli.size/3, -heli.size/4, heli.size/2, heli.size/3);
      
      // Rotor blur effect
      ctx.strokeStyle = 'rgba(159, 203, 220, 0.3)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(0, -heli.size/3, heli.size * 0.8, 0, Math.PI * 2);
      ctx.stroke();
      
      // Tail rotor
      ctx.fillStyle = '#17214a';
      ctx.fillRect(heli.size/3, -heli.size/8, heli.size/4, heli.size/4);
      
      ctx.restore();
      
      // Continue game loop
      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };
    
    gameLoop();
    
    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [gameState]);
  
  const resetGame = () => {
    helicopter.current = {
      x: 100,
      y: 300,
      vx: 0,
      vy: 0,
      angle: 0,
      size: 40
    };
    setScore(0);
    setGameState('playing');
  };
  
  const toggleInstructions = () => {
    setShowInstructions(!showInstructions);
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-blue-400 to-blue-200 overflow-hidden">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 cursor-none"
      />
      
      {/* UI Overlay */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center pointer-events-auto">
          <div className="flex items-center space-x-4">
            <Link 
              to="/"
              className="bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-all duration-200"
            >
              <FiHome size={20} />
            </Link>
            <button
              onClick={toggleInstructions}
              className="bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-all duration-200"
            >
              <FiInfo size={20} />
            </button>
          </div>
          
          <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full text-white font-bold text-xl">
            Score: {score}
          </div>
          
          <button
            onClick={resetGame}
            className="bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-all duration-200"
          >
            <FiRotateCcw size={20} />
          </button>
        </div>
        
        {/* Instructions */}
        {showInstructions && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur-sm p-6 rounded-2xl shadow-xl max-w-md pointer-events-auto"
          >
            <h3 className="text-xl font-bold text-midnight-navy mb-4">🚁 Blacksand Pilot Training</h3>
            <div className="space-y-2 text-sm text-shadow-gray">
              <p><strong>Mouse:</strong> Move to guide helicopter</p>
              <p><strong>WASD/Arrows:</strong> Precise control</p>
              <p><strong>Goal:</strong> Avoid obstacles and fly as far as possible!</p>
              <p><strong>Spacebar:</strong> Restart after crash</p>
            </div>
            <button
              onClick={() => setShowInstructions(false)}
              className="mt-4 bg-aero-blue text-white px-4 py-2 rounded-lg hover:bg-aero-blue/90 transition-colors duration-200"
            >
              Start Flying!
            </button>
          </motion.div>
        )}
        
        {/* Game Over */}
        {gameState === 'crashed' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-auto"
          >
            <div className="bg-white/95 backdrop-blur-sm p-8 rounded-3xl shadow-2xl text-center max-w-md">
              <h2 className="text-3xl font-bold text-midnight-navy mb-4">🚁 Mission Complete!</h2>
              <p className="text-xl text-shadow-gray mb-2">Final Score: <span className="font-bold text-aero-blue">{score}</span></p>
              <p className="text-sm text-shadow-gray mb-6">
                {score > 100 ? "Excellent piloting skills!" : 
                 score > 50 ? "Good flying, pilot!" : 
                 "Keep practicing, future aviator!"}
              </p>
              <div className="space-y-3">
                <button
                  onClick={resetGame}
                  className="w-full bg-aero-blue text-white py-3 rounded-xl font-semibold hover:bg-aero-blue/90 transition-colors duration-200"
                >
                  Fly Again (Spacebar)
                </button>
                <Link 
                  to="/"
                  className="block w-full bg-midnight-navy text-white py-3 rounded-xl font-semibold hover:bg-midnight-navy/90 transition-colors duration-200 text-center"
                >
                  Return to Base
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default HelicopterGame;